import { useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';

export interface PaymentRequest {
    requestId: string;
    userId: string;
    userName: string;
    userEmail: string;
    userType?: string;
    iban?: string;
    bic?: string;
    affiliateCode: string;
    walletBalance: number;
    amount: number;
    requestedAt: string;
}

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const fmtAmount = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Pending affiliate-wallet payout requests (sellers and buyers share the same flow).
export default function PaymentRequestsCard({
    requests,
    onMarkedSent,
}: {
    requests: PaymentRequest[];
    onMarkedSent: (userId: string) => void;
}) {
    const [markingId, setMarkingId] = useState<string | null>(null);
    const [confirmMarkId, setConfirmMarkId] = useState<string | null>(null);

    const handleMarkSent = async (userId: string) => {
        setMarkingId(userId);
        try {
            await axios.post(
                `${CONFIG.apiUrl}/admin/affiliate-payment-requests/${userId}/mark-sent`,
                {},
                authHeaders()
            );
            onMarkedSent(userId);
        } catch {
        } finally {
            setMarkingId(null);
            setConfirmMarkId(null);
        }
    };

    if (requests.length === 0) return null;

    return (
        <Card extra="p-5">
            <p className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                Demandes de virement ({requests.length})
            </p>
            <div className="flex flex-col gap-3">
                {requests.map((req) => (
                    <div key={req.requestId} className="flex items-center justify-between gap-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex-wrap">
                        <div>
                            <p className="font-semibold text-navy-700 dark:text-white">
                                {req.userName || '—'}{' '}
                                <span className="font-mono text-xs text-brand-500">{req.affiliateCode}</span>
                            </p>
                            <p className="text-xs text-gray-500">{req.userEmail}</p>
                            <p className="mt-0.5 font-mono text-xs text-gray-600">
                                IBAN : {req.iban || <span className="font-sans text-red-500">non renseigné</span>}
                                {req.bic ? ` · BIC : ${req.bic}` : ''}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                                Demandé le {new Date(req.requestedAt).toLocaleDateString('fr-FR')} · Solde actuel :{' '}
                                <span className="font-semibold text-orange-600">{fmtAmount(req.walletBalance)} €</span>
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-green-600">{fmtAmount(req.amount)} €</span>
                            {confirmMarkId === req.userId ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Confirmer l'envoi ?</span>
                                    <button
                                        onClick={() => handleMarkSent(req.userId)}
                                        disabled={markingId === req.userId}
                                        className="rounded-md bg-green-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-50"
                                    >
                                        {markingId === req.userId ? '...' : 'Oui, envoyé'}
                                    </button>
                                    <button
                                        onClick={() => setConfirmMarkId(null)}
                                        className="rounded-md bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmMarkId(req.userId)}
                                    className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
                                >
                                    Marquer comme envoyé
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}
